import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useModuleConfig } from '../context/ModuleConfigContext';
import { useBookingData } from '../context/BookingDataContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPropertyName } from '../utils/propertyUtils';
import { formatGuestBreakdown, rawGuestValue } from '../utils/formatGuests';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  Plus, Loader2, Phone, User, Home, CreditCard, X,
  ArrowDownToLine, ArrowUpFromLine, Globe, RefreshCw,
  Bell, AlertTriangle, Edit3, History,
  Eye, Download, Check, XCircle, ShieldCheck, Ban,
  LogIn, UserX, Clock, MessageCircle, MapPin
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { bookingsAPI, bookingApprovalAPI, guestDocumentAPI, whatsappConversationAPI } from '../services/api';
import { getToken } from '../services/tokenService';
import SyncIndicator from '../components/SyncIndicator';
import AppHeader from '../components/AppHeader';
import OtaLogo from '../components/OtaLogo';
import AadhaarCaptureModal from '../components/AadhaarCaptureModal';
import GuestDocumentCard from '../components/GuestDocumentCard';
import { WhatsAppJourneyBadge } from '../components/WhatsAppJourneyBadge';
import { toast } from '../hooks/use-toast';

const FILTER_OPTIONS = [
  { value: 'booking', label: 'Booking Date', icon: CalendarIcon },
  { value: 'checkin', label: 'Check In', icon: ArrowDownToLine },
  { value: 'checkout', label: 'Check Out', icon: ArrowUpFromLine },
];

const CalendarScreen = () => {
  const { selectedProperty } = useAuth();
  const { isEnabled: isModuleEnabled } = useModuleConfig();
  const chatsEnabled = isModuleEnabled('chats');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Use shared booking data context
  const {
    allBookings,
    notifications,
    loading,
    dataSource,
    refreshData,
    getCalendarCounts,
    totalBookings,
    wsConnected,
    unreadNotifications,
    markNotificationRead,
    clearNotifications,
  } = useBookingData();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarCounts, setCalendarCounts] = useState({ checkin: {}, checkout: {}, booking: {} });
  const [monthlyBookings, setMonthlyBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filterType, setFilterType] = useState('checkin'); // Default to Check-in
  const [selectedBooking, setSelectedBooking] = useState(null);
  // iter111 — Guest WhatsApp journey badge statuses keyed by booking_id
  const [journeyStatuses, setJourneyStatuses] = useState({});
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(null);

  // ─── Cancel booking flow ─────────────────────────────────
  // Step 1 → confirm intent. Step 2 → enter optional refund. null = closed.
  const [cancelStep, setCancelStep] = useState(null); // 'confirm' | 'refund' | null
  const [cancelTarget, setCancelTarget] = useState(null);
  const [refundAmount, setRefundAmount] = useState('0');
  const [cancelling, setCancelling] = useState(false);

  // iter92 — Owner approval workflow state
  const [approveTarget, setApproveTarget] = useState(null);     // booking awaiting confirm
  const [rejectTarget, setRejectTarget] = useState(null);       // booking awaiting reject
  const [rejectReason, setRejectReason] = useState('');
  const [rejectOther, setRejectOther] = useState('');
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);

  // iter106 — Guest Aadhaar capture + check-in workflow state
  const [aadhaarTarget, setAadhaarTarget] = useState(null);     // booking awaiting upload
  const [checkinConfirmTarget, setCheckinConfirmTarget] = useState(null); // booking awaiting check-in confirm
  const [noShowTarget, setNoShowTarget] = useState(null);       // booking awaiting no-show
  const [checkinSubmitting, setCheckinSubmitting] = useState(false);

  const isOtaBooking = (booking) => {
    const src = (booking?.booking_source || booking?.source || booking?.ota_source || '').toLowerCase();
    return src && !['manual', 'walk-in', 'walkin', ''].includes(src);
  };

  const isWalkInBooking = (booking) => {
    const src = (booking?.booking_source || booking?.source || booking?.ota_source || '').toLowerCase();
    return ['walk-in', 'walkin', 'manual'].includes(src) || (booking?.booking_id || '').startsWith('WB-');
  };

  // iter98 — Tighten Approve/Reject visibility: OTA + Owner=Pending + !locked + check-in IN FUTURE + active status
  // iter106 — Approve/Reject buttons are now only shown BEFORE the check-in date.
  //          On the check-in date (or later), they are replaced by Check-in Confirmed / No Show.
  const todayIsoLocal = () => {
    // YYYY-MM-DD in local (browser) TZ — used purely for lexicographic comparison against the bare
    // date strings the backend returns. No Date() math involved that could drift.
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const isCheckinToday = (booking) => {
    const ci = (booking?.check_in_date || booking?.check_in || '').toString().slice(0, 10);
    if (!ci) return false;
    if (typeof booking?.is_checkin_today === 'boolean') return booking.is_checkin_today;
    return ci === todayIsoLocal();
  };
  const isCheckinPast = (booking) => {
    const ci = (booking?.check_in_date || booking?.check_in || '').toString().slice(0, 10);
    if (!ci) return false;
    if (typeof booking?.is_checkin_past === 'boolean') return booking.is_checkin_past;
    return ci < todayIsoLocal();
  };
  const isCheckinDateOrPast = (booking) => isCheckinToday(booking) || isCheckinPast(booking);
  const isActiveBookingStatus = (booking) => {
    const bs = (booking?.booking_status || '').toString().toLowerCase().replace(/[\s_-]/g, '');
    if (['cancelled', 'canceled', 'checkedout', 'noshow'].includes(bs)) return false;
    if (booking?.is_checked_out) return false;
    return true;
  };
  const showApprovalControls = (booking) => {
    if (!isOtaBooking(booking)) return false;
    if ((booking?.owner_status || 'Pending') !== 'Pending') return false;
    if (booking?.approval_locked) return false;
    if (isCheckinDateOrPast(booking)) return false;        // → Check-in / No-show takes over
    if (!isActiveBookingStatus(booking)) return false;
    return true;
  };
  // iter106+ — Show Check-in Confirmed / No-show ONLY on the check-in day itself.
  // Past check-in dates without a decision render the "Check-in Pending" badge
  // (handled in the JSX) — buttons are not surfaced retroactively.
  const showCheckinControls = (booking) => {
    if (!isCheckinToday(booking)) return false;            // STRICT: today only
    if (booking?.checkin_decision) return false;           // already decided
    if (!isActiveBookingStatus(booking)) return false;
    if (isOtaBooking(booking) && booking?.owner_status === 'Rejected' && booking?.sneha_status !== 'Approved') {
      return false;
    }
    return true;
  };
  // iter106+ — "Check-in Pending" badge for past check-in dates with no decision.
  const showCheckinPendingBadge = (booking) => {
    if (!isCheckinPast(booking)) return false;
    if (booking?.checkin_decision) return false;
    if (!isActiveBookingStatus(booking)) return false;
    if (isOtaBooking(booking) && booking?.owner_status === 'Rejected' && booking?.sneha_status !== 'Approved') {
      return false;
    }
    return true;
  };

  const submitOwnerApprove = async () => {
    if (!approveTarget || approvalSubmitting) return;
    setApprovalSubmitting(true);
    try {
      const res = await bookingApprovalAPI.approve(approveTarget.booking_id);
      const updated = res?.RESULT || res?.data?.RESULT || {};
      toast({ title: 'Booking approved', description: `Booking ${approveTarget.booking_id} approved by Hotel Owner` });
      setSelectedBooking((prev) => (prev && prev.booking_id === approveTarget.booking_id ? { ...prev, ...updated } : prev));
      setApproveTarget(null);
      try { await refreshData?.(); } catch (_) { /* ignore */ }
    } catch (err) {
      toast({ title: 'Approve failed', description: err?._friendlyMessage || 'Please try again.', variant: 'destructive' });
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const submitOwnerReject = async () => {
    if (!rejectTarget || approvalSubmitting) return;
    if (!rejectReason) {
      toast({ title: 'Reason required', variant: 'destructive' });
      return;
    }
    if (rejectReason === 'Other' && !rejectOther.trim()) {
      toast({ title: 'Please specify the reason', variant: 'destructive' });
      return;
    }
    setApprovalSubmitting(true);
    try {
      const res = await bookingApprovalAPI.reject(rejectTarget.booking_id, rejectReason, rejectOther.trim() || null);
      const updated = res?.RESULT || res?.data?.RESULT || {};
      toast({ title: 'Booking rejected', description: `Booking ${rejectTarget.booking_id} rejected by Hotel Owner` });
      setSelectedBooking((prev) => (prev && prev.booking_id === rejectTarget.booking_id ? { ...prev, ...updated } : prev));
      setRejectTarget(null); setRejectReason(''); setRejectOther('');
      try { await refreshData?.(); } catch (_) { /* ignore */ }
    } catch (err) {
      toast({ title: 'Reject failed', description: err?._friendlyMessage || 'Please try again.', variant: 'destructive' });
    } finally {
      setApprovalSubmitting(false);
    }
  };

  // iter106 — Owner check-in confirm / no-show submits
  const submitCheckinConfirm = async () => {
    if (!checkinConfirmTarget || checkinSubmitting) return;
    setCheckinSubmitting(true);
    try {
      const res = await guestDocumentAPI.checkinConfirm(checkinConfirmTarget.booking_id);
      const updated = res?.data?.RESULT || res?.RESULT || {};
      toast({ title: 'Check-in confirmed', description: `Booking ${checkinConfirmTarget.booking_id} marked as checked in.` });
      setSelectedBooking((prev) => (prev && prev.booking_id === checkinConfirmTarget.booking_id ? { ...prev, ...updated } : prev));
      setCheckinConfirmTarget(null);
      try { await refreshData?.(); } catch (_) { /* ignore */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?._friendlyMessage || 'Please try again.';
      toast({ title: 'Check-in failed', description: detail, variant: 'destructive' });
    } finally {
      setCheckinSubmitting(false);
    }
  };

  const submitNoShow = async () => {
    if (!noShowTarget || checkinSubmitting) return;
    setCheckinSubmitting(true);
    try {
      const res = await guestDocumentAPI.noShow(noShowTarget.booking_id);
      const updated = res?.data?.RESULT || res?.RESULT || {};
      toast({ title: 'Marked as No Show', description: `Booking ${noShowTarget.booking_id}` });
      setSelectedBooking((prev) => (prev && prev.booking_id === noShowTarget.booking_id ? { ...prev, ...updated } : prev));
      setNoShowTarget(null);
      try { await refreshData?.(); } catch (_) { /* ignore */ }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?._friendlyMessage || 'Please try again.';
      toast({ title: 'No-show failed', description: detail, variant: 'destructive' });
    } finally {
      setCheckinSubmitting(false);
    }
  };

  const startCancelFlow = (booking) => {
    setCancelTarget(booking);
    setRefundAmount('0');
    setCancelStep('confirm');
  };

  const submitCancel = async () => {
    if (!cancelTarget || cancelling) return;
    const refund = Math.max(0, parseFloat(refundAmount || '0') || 0);
    setCancelling(true);
    try {
      const res = await bookingsAPI.cancel(cancelTarget.booking_id, refund);
      const result = res.data?.RESULT || {};
      toast({
        title: 'Booking cancelled',
        description: refund > 0
          ? `Refund ₹${refund.toLocaleString('en-IN')} marked. WhatsApp queued.`
          : 'Cancellation WhatsApp queued.',
      });
      // Optimistically reflect on the open popup so user sees the change immediately
      setSelectedBooking((prev) => prev && prev.booking_id === cancelTarget.booking_id
        ? { ...prev, is_cancelled: true, booking_status: 'cancelled', refund_amount: refund, cancelled_at: result.cancelled_at }
        : prev,
      );
      setCancelStep(null);
      setCancelTarget(null);
      // Refresh list in the background — don't block the UI
      try { await refreshData?.(); } catch (_) { /* ignore */ }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      toast({
        title: status === 409 ? 'Already cancelled' : 'Cancellation failed',
        description: detail || 'Please try again.',
        variant: 'destructive',
      });
      if (status === 409) { setCancelStep(null); setCancelTarget(null); }
    } finally {
      setCancelling(false);
    }
  };

  // Check if booking is manual (created via "+" button)
  const isManualBooking = (booking) => {
    const src = (booking?.booking_source || booking?.source || booking?.ota_source || '').toLowerCase();
    return src === 'manual' || src === 'walk-in';
  };

  // Invoice actions
  const handleInvoicePreview = (e, booking) => {
    e.stopPropagation();
    const token = getToken();
    const url = `${bookingsAPI.getInvoiceUrl(booking.booking_id)}?token=${token}`;
    setInvoicePreview({ url, bookingId: booking.booking_id });
  };

  const handleInvoiceDownload = (e, booking) => {
    e.stopPropagation();
    const token = getToken();
    const url = `${bookingsAPI.getInvoiceDownloadUrl(booking.booking_id)}&token=${token}`;
    window.open(url, '_blank');
  };

  // Check for deep link booking ID
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (bookingId && allBookings.length > 0) {
      // Find booking by ID in cached data
      const booking = allBookings.find(b => b.booking_id === bookingId || b.id === bookingId);
      if (booking) {
        setSelectedBooking(booking);
        setShowBookingPopup(true);
        // Set the date to the booking's relevant date
        const dateStr = (booking.check_in_date || booking.check_in || '').split('T')[0];
        if (dateStr) {
          const date = new Date(dateStr);
          setCurrentDate(date);
          setSelectedDate(date);
        }
      }
    }
  }, [searchParams, allBookings]);

  // Filter bookings for current month view (computed from cached data)
  const localFilterBookingsForMonth = useCallback((bookings, monthStart, monthEnd, filter) => {
    const startStr = format(monthStart, 'yyyy-MM-dd');
    const endStr = format(monthEnd, 'yyyy-MM-dd');
    
    return bookings.filter(b => {
      let dateToCheck = '';
      switch (filter) {
        case 'checkout':
          // Handle both field name formats
          dateToCheck = (b.check_out_date || b.check_out || '').split('T')[0];
          break;
        case 'booking':
          dateToCheck = (b.booked_on_date || b.booking_date || b.created_at || '').split('T')[0];
          break;
        case 'checkin':
        default:
          // Handle both field name formats (Admin Panel uses check_in, old bookings use check_in_date)
          dateToCheck = (b.check_in_date || b.check_in || '').split('T')[0];
      }
      return dateToCheck >= startStr && dateToCheck <= endStr;
    });
  }, []);

  // Update displayed data when month/filter changes (from cached data - INSTANT)
  useEffect(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startStr = format(monthStart, 'yyyy-MM-dd');
    const endStr = format(monthEnd, 'yyyy-MM-dd');
    
    console.log(`[Calendar] Month: ${startStr} to ${endStr}, Total bookings: ${allBookings.length}`);
    
    // Calculate counts from cached data (instant)
    const counts = getCalendarCounts(startStr, endStr);
    setCalendarCounts(counts);
    
    // Filter bookings for month view (instant)
    const filtered = localFilterBookingsForMonth(allBookings, monthStart, monthEnd, filterType);
    console.log(`[Calendar] Filtered ${filtered.length} bookings for ${format(currentDate, 'MMMM yyyy')}`);
    setMonthlyBookings(filtered);
  }, [allBookings, currentDate, filterType, getCalendarCounts, localFilterBookingsForMonth]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await refreshData();
    setRefreshing(false);
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const days = eachDayOfInterval({ start, end });
    
    const startDay = start.getDay();
    const paddingDays = Array(startDay).fill(null);
    
    return [...paddingDays, ...days];
  }, [currentDate]);

  // Get count for a specific day based on current filter
  const getDayCount = useCallback((date) => {
    if (!date) return 0;
    const dateStr = format(date, 'yyyy-MM-dd');
    const counts = calendarCounts[filterType] || {};
    return counts[dateStr] || 0;
  }, [calendarCounts, filterType]);

  // iter135 — Per-day booking-source breakdown map, computed once from the
  // already-fetched monthly bookings. Zero extra API cost.
  // Shape: { "YYYY-MM-DD": { "Booking.com": 2, "Walk-in": 1, ... } }
  const daySourceBreakdown = useMemo(() => {
    const map = {};
    if (!Array.isArray(monthlyBookings)) return map;
    monthlyBookings.forEach((b) => {
      let dateStr = '';
      if (filterType === 'checkout') {
        dateStr = (b.check_out_date || b.check_out || '').split('T')[0];
      } else if (filterType === 'booking') {
        dateStr = (b.booked_on_date || b.booking_date || b.created_at || '').split('T')[0];
      } else {
        dateStr = (b.check_in_date || b.check_in || '').split('T')[0];
      }
      if (!dateStr) return;
      // Ignore cancelled/rejected — must mirror get_calendar_counts server logic
      const status = (b.booking_status || '').toLowerCase();
      if (status === 'cancelled' || status === 'rejected') return;
      // Normalise source: strip trailing " - <ota>" fragments; group walk-ins.
      let src = (b.booking_source || b.ota_source || b.ota_name || '').trim();
      if (!src) src = 'Direct';
      // Common canonicalisation
      const lc = src.toLowerCase();
      if (lc === 'walkin' || lc === 'walk-in' || lc === 'walk in' || lc === 'manual') src = 'Walk-in';
      else if (lc.startsWith('booking')) src = 'Booking.com';
      else if (lc.includes('mmt') || lc.includes('makemytrip')) src = 'MMT';
      else if (lc.startsWith('agoda')) src = 'Agoda';
      else if (lc.startsWith('expedia')) src = 'Expedia';
      else if (lc.startsWith('airbnb')) src = 'Airbnb';
      else if (lc.startsWith('goibibo')) src = 'Goibibo';
      map[dateStr] = map[dateStr] || {};
      map[dateStr][src] = (map[dateStr][src] || 0) + 1;
    });
    return map;
  }, [monthlyBookings, filterType]);

  // Sorted list of {source, count} for a given date — used in calendar cells.
  const getDaySources = useCallback((date) => {
    if (!date) return [];
    const dateStr = format(date, 'yyyy-MM-dd');
    const entries = Object.entries(daySourceBreakdown[dateStr] || {});
    // Sort by count desc then source name asc for stable UI
    return entries
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source));
  }, [daySourceBreakdown]);

  // Filter monthly bookings by selected date
  const filteredMonthlyBookings = useMemo(() => {
    if (!selectedDate) return monthlyBookings;
    
    const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
    return monthlyBookings.filter(booking => {
      let dateToCheck;
      switch (filterType) {
        case 'checkout':
          // Handle both field name formats
          dateToCheck = (booking.check_out_date || booking.check_out || '').split('T')[0];
          break;
        case 'booking':
          dateToCheck = (booking.booked_on_date || booking.booking_date || booking.created_at || '').split('T')[0];
          break;
        case 'checkin':
        default:
          // Handle both field name formats
          dateToCheck = (booking.check_in_date || booking.check_in || '').split('T')[0];
      }
      return dateToCheck === selectedDateStr;
    });
  }, [monthlyBookings, selectedDate, filterType]);

  // iter111 — Fetch WhatsApp Journey statuses for visible OTA bookings (bulk, one round-trip).
  // Admin Panel pushes updates server-side; the frontend refetches on booking list change /
  // pull-to-refresh so badges reflect the latest snapshot without periodic polling.
  useEffect(() => {
    const visible = selectedDate ? filteredMonthlyBookings : monthlyBookings;
    const otaIds = visible
      .filter((b) => isOtaBooking(b))
      .map((b) => b.booking_id)
      .filter(Boolean);
    if (otaIds.length === 0) {
      setJourneyStatuses({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await whatsappConversationAPI.bulkStatus(otaIds);
        if (!cancelled) setJourneyStatuses(res?.data?.RESULT || {});
      } catch (e) {
        // Silent — badges just fall back to "not_started" ⚪
      }
    })();
    return () => { cancelled = true; };
  }, [monthlyBookings, filteredMonthlyBookings, selectedDate]);

  // Get status info with cancellation/amendment styling
  const getStatusInfo = (booking) => {
    if (booking.is_cancelled) {
      return { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', popupBg: 'bg-red-50' };
    }
    if (booking.is_amended || booking.has_amendments) {
      return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Amended', popupBg: 'bg-blue-50' };
    }
    const status = booking.booking_status || booking.status;
    if (status === 'cancelled' || status === 'rejected') {
      return { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', popupBg: 'bg-red-50' };
    }
    if (booking.is_checked_out || status === 'checked_out') {
      return { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Checked Out', popupBg: 'bg-white' };
    }
    if (booking.is_checked_in || status === 'checked_in') {
      return { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Checked In', popupBg: 'bg-white' };
    }
    return { bg: 'bg-[#EEF1FC]', text: 'text-[#1B2A7B]', label: 'Confirmed', popupBg: 'bg-white' };
  };

  // Get room display text
  const getRoomDisplay = (rooms, roomType) => {
    // Treat dash / hyphen / em-dash variants as "no room type" and
    // fall through to the rooms[] breakdown so the card renders a
    // real name instead of a bare "-".
    const t = String(roomType || '').trim();
    if (t && !/^[-–—]+$/.test(t)) return t;
    if (!rooms || rooms.length === 0) return 'No rooms';
    return rooms.map(r => `${r.room_name || r.room_type_name || 'Room'} (${r.no_of_rooms || r.room_count || 1})`).join(', ');
  };

  // Check if a field was changed (for amendment highlighting)
  const isFieldChanged = (booking, fieldName) => {
    return booking.changed_fields?.includes(fieldName);
  };

  const handleDateClick = (date) => {
    if (!date) return;
    if (selectedDate && isSameDay(date, selectedDate)) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  const handleFilterChange = (value) => {
    setFilterType(value);
    setSelectedDate(null);
  };

  const handleBookingClick = (booking) => {
    setSelectedBooking(booking);
    setShowBookingPopup(true);
  };

  const closeBookingPopup = () => {
    setShowBookingPopup(false);
    setSelectedBooking(null);
  };

  const handleNotificationClick = (notification, index, e) => {
    e.stopPropagation(); // Prevent clickaway from triggering
    setShowNotifications(false);
    
    // Find booking in cached data
    const booking = allBookings.find(b => 
      b.booking_id === notification.booking_id || b.id === notification.booking_id
    );
    
    if (booking) {
      setSelectedBooking(booking);
      setShowBookingPopup(true);
      // Set the date to the booking's relevant date
      const dateStr = (booking.check_in_date || booking.check_in || '').split('T')[0];
      if (dateStr) {
        const date = new Date(dateStr);
        setCurrentDate(date);
        setSelectedDate(date);
      }
    } else {
      // Fallback to URL navigation for bookings not in current cache
      navigate(`/calendar?bookingId=${notification.booking_id}`);
    }
  };

  const selectedFilter = FILTER_OPTIONS.find(f => f.value === filterType);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pb-24" data-testid="calendar-screen">
      {/* Premium App Header — profile + property switcher */}
      <AppHeader />

      {/* Sub-header: month nav, sync, notifications, filters */}
      <div className="sticky top-[64px] z-20 bg-[#1B2A7B] shadow-premium">
        <div className="px-4 py-4">
          {/* Month Navigation, Refresh, and Notifications */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setCurrentDate(subMonths(currentDate, 1))}
              className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all hover:scale-105"
              data-testid="prev-month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <h2 className="text-lg font-semibold text-white tracking-tight" data-testid="current-month">
                {format(currentDate, 'MMMM yyyy')}
              </h2>
              <span className="text-[10px] uppercase tracking-wider text-gold/80 font-medium" data-testid="month-booking-count">
                {monthlyBookings.length} bookings
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Property Sync Indicator */}
              <SyncIndicator propertyId={selectedProperty?.id} />
              
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="p-2 rounded-lg bg-navy-light text-white hover:bg-opacity-80 transition-colors relative"
                  data-testid="notification-bell"
                >
                  <Bell className="w-5 h-5" />
                  {unreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center font-bold">
                      {unreadNotifications > 9 ? '9+' : unreadNotifications}
                    </span>
                  )}
                </button>
                
                {/* Real-time Notifications Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                    <div className="sticky top-0 bg-white px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      {notifications.length > 0 && (
                        <button
                          onClick={clearNotifications}
                          className="text-xs font-medium text-[#1B2A7B] hover:text-[#1B2A7B]"
                          data-testid="clear-notifications-btn"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center" data-testid="notifications-empty-state">
                        <div className="mx-auto w-10 h-10 rounded-full bg-[#EEF1FC] flex items-center justify-center mb-2">
                          <Bell className="w-5 h-5 text-[#1B2A7B]" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">No notifications</p>
                        <p className="text-xs text-gray-500 mt-1">You&apos;re all caught up!</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {notifications.slice(0, 20).map((notif, idx) => (
                          <button
                            key={notif.id}
                            onClick={(e) => {
                              // iter142 — Wire click → open the related
                              // booking AND mark it read (persist to server).
                              handleNotificationClick(notif, idx, e);
                              markNotificationRead(notif.id);
                            }}
                            className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${!notif.read ? 'bg-blue-50/50' : ''}`}
                            data-testid={`notification-item-${notif.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-1.5 rounded-full ${notif.type === 'new_booking' ? 'bg-[#EEF1FC]' : notif.type === 'booking_cancelled' ? 'bg-rose-100' : 'bg-blue-100'}`}>
                                {notif.type === 'new_booking' ? (
                                  <CalendarIcon className="w-4 h-4 text-[#1B2A7B]" />
                                ) : notif.type === 'booking_cancelled' ? (
                                  <CalendarIcon className="w-4 h-4 text-rose-600" />
                                ) : (
                                  <User className="w-4 h-4 text-blue-600" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                                <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                  {new Date(notif.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                              {!notif.read && <div className="mt-2 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all hover:scale-105 disabled:opacity-50"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => setCurrentDate(addMonths(currentDate, 1))}
                className="p-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-all hover:scale-105"
                data-testid="next-month"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filter Buttons - Button Style */}
          <div className="flex gap-2 items-center justify-center" data-testid="calendar-filter-buttons">
            {FILTER_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isActive = filterType === option.value;
              return (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange(option.value)}
                  className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm
                    transition-all duration-200 shadow-sm
                    ${isActive 
                      ? 'bg-[#1B2A7B] text-white shadow-[#1B2A7B]/30 border border-[#1B2A7B]' 
                      : 'bg-navy-light text-gray-200 border border-navy-light hover:bg-opacity-80 hover:border-gray-400'
                    }
                  `}
                  data-testid={`filter-btn-${option.value}`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-300'}`} />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Week Day Headers */}
        <div className="grid grid-cols-7 px-2 pb-3 bg-navy-light">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-xs text-gray-300 py-2 font-medium">
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
        </div>
      ) : (
        <div className="px-2 py-2">
          <div className="grid grid-cols-7 gap-0.5" key={`calendar-grid-${filterType}-${format(currentDate, 'yyyy-MM')}`}>
            {calendarDays.map((day, index) => {
              const count = day ? getDayCount(day) : 0;
              const hasBookings = count > 0;
              const isSelected = selectedDate && day && isSameDay(day, selectedDate);
              const isToday = day && isSameDay(day, new Date());
              const sources = day ? getDaySources(day) : [];

              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(day)}
                  disabled={!day}
                  className={`
                    min-h-[64px] rounded-lg flex flex-col items-stretch justify-start px-1.5 pt-1.5 pb-1 relative transition-all
                    ${!day ? 'invisible' : ''}
                    ${isSelected ? 'bg-[#1B2A7B] text-white shadow-[#1B2A7B]/30 ring-2 ring-[#1B2A7B]/40' : hasBookings ? 'bg-[#EEF1FC] border border-[#1B2A7B]/30' : 'bg-white border border-gray-200'}
                    ${isToday && !isSelected ? 'ring-2 ring-navy' : ''}
                    hover:shadow-md text-left
                  `}
                  data-testid={day ? `calendar-day-${format(day, 'yyyy-MM-dd')}` : null}
                >
                  {day && (
                    <>
                      {/* iter150 — Compact calendar cell.
                          • Cell height reduced (min-h 104 → 64) so the grid
                            looks like short rectangles instead of tall bars.
                          • Date number anchored to the top-LEFT corner (no
                            longer vertically centered).
                          • Row gap tightened (gap-1 → gap-0.5) so the whole
                            calendar packs onto one mobile screen.
                          • Booking-source chips (iter149) unchanged in look;
                            still wrap-flex, but now flow under the date. */}
                      <span
                        className={`self-start text-sm font-semibold leading-none ${isSelected ? 'text-white' : isToday ? 'text-navy font-bold' : hasBookings ? 'text-[#1B2A7B]' : 'text-gray-700'}`}
                      >
                        {format(day, 'd')}
                      </span>
                      {hasBookings && (
                        <div
                          className="mt-1 flex flex-wrap justify-center gap-[2px] overflow-hidden w-full"
                          data-testid={`day-sources-${format(day, 'yyyy-MM-dd')}`}
                        >
                          {sources.map(({ source, count: c }) => (
                            <span
                              key={source}
                              className={`leading-none whitespace-nowrap text-[9px] sm:text-[10px] font-medium px-1 py-[1px] rounded ${
                                isSelected
                                  ? 'bg-white/20 text-white'
                                  : 'bg-white/70 text-[#1B2A7B] border border-[#1B2A7B]/20'
                              }`}
                              title={`${c} ${source}`}
                            >
                              <span className="font-bold">{c}</span> {source}
                            </span>
                          ))}
                        </div>
                      )}
                      {hasBookings && (
                        <span
                          className={`mt-0.5 self-center text-sm font-bold leading-none ${isSelected ? 'text-white' : 'text-[#1B2A7B]'}`}
                          data-testid={`day-total-${format(day, 'yyyy-MM-dd')}`}
                          aria-label={`Total bookings: ${count}`}
                        >
                          {count}
                        </span>
                      )}
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Legend and Selected Date Info */}
      <div className="px-4 py-2 flex items-center justify-between text-xs text-gray-500 border-b border-gray-200 bg-white">
        <span className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-[#1B2A7B]" />
          Numbers show {selectedFilter?.label.toLowerCase()} count
        </span>
        {selectedDate && (
          <button 
            onClick={() => setSelectedDate(null)}
            className="text-[#1B2A7B] hover:text-[#1B2A7B] font-medium flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear filter
          </button>
        )}
      </div>

      {/* Monthly Bookings List */}
      <div className="px-4 py-4" data-testid="monthly-bookings-list">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {selectedDate 
              ? `${selectedFilter?.label}s on ${format(selectedDate, 'MMM d, yyyy')}`
              : `All ${selectedFilter?.label}s - ${format(currentDate, 'MMMM yyyy')}`
            }
          </h3>
          <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-lg">
            {selectedDate ? filteredMonthlyBookings.length : monthlyBookings.length} booking{(selectedDate ? filteredMonthlyBookings.length : monthlyBookings.length) !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-[#1B2A7B] animate-spin" />
          </div>
        ) : (selectedDate ? filteredMonthlyBookings : monthlyBookings).length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">
              {selectedDate 
                ? `No ${selectedFilter?.label.toLowerCase()}s for this date`
                : `No bookings for ${format(currentDate, 'MMMM yyyy')}`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {(selectedDate ? filteredMonthlyBookings : monthlyBookings).map((booking, index) => {
              const statusInfo = getStatusInfo(booking);
              const isCancelled = booking.is_cancelled;
              const isAmended = booking.is_amended || booking.has_amendments;
              
              return (
                <div
                  key={booking.booking_id || index}
                  onClick={() => handleBookingClick(booking)}
                  role="button"
                  tabIndex={0}
                  className={`w-full rounded-xl border p-4 text-left transition-all hover:shadow-md cursor-pointer ${
                    isCancelled 
                      ? 'bg-red-50 border-red-200 hover:border-red-300' 
                      : isAmended 
                        ? 'bg-blue-50 border-blue-200 hover:border-blue-300'
                        : 'bg-white border-gray-200 hover:border-[#1B2A7B]'
                  }`}
                  data-testid={`booking-card-${booking.booking_id || index}`}
                >
                  {/* Booking ID and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Booking ID</p>
                      <p className="font-semibold text-navy">{booking.booking_id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Invoice Action Icons — only for manual bookings */}
                      {isManualBooking(booking) && !isCancelled && (
                        <div className="flex items-center gap-1 mr-1" data-testid={`invoice-actions-${booking.booking_id}`}>
                          <button
                            onClick={(e) => handleInvoicePreview(e, booking)}
                            className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                            title="Preview Invoice"
                            data-testid={`preview-invoice-${booking.booking_id}`}
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={(e) => handleInvoiceDownload(e, booking)}
                            className="p-1.5 rounded-lg bg-[#EEF1FC] text-[#1B2A7B] hover:bg-[#EEF1FC] transition-colors"
                            title="Download Invoice PDF"
                            data-testid={`download-invoice-${booking.booking_id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      {isAmended && (
                        <span className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
                          <Edit3 className="w-3 h-3" />
                          Amended
                        </span>
                      )}
                      <OtaLogo otaName={booking.ota_source} />
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusInfo.bg} ${statusInfo.text}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                  </div>

                  {/* iter92 — Owner Approval Controls (OTA bookings only, Pending state, BEFORE check-in date) */}
                  {showApprovalControls(booking) && (
                    <div className="flex items-center gap-2 mb-3" data-testid={`approval-controls-${booking.booking_id}`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); setApproveTarget(booking); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#EEF1FC] text-[#1B2A7B] border border-[#1B2A7B]/30 text-xs font-semibold hover:bg-[#EEF1FC] transition-colors"
                        data-testid={`owner-approve-btn-${booking.booking_id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setRejectTarget(booking); setRejectReason(''); setRejectOther(''); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100 transition-colors"
                        data-testid={`owner-reject-btn-${booking.booking_id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                    </div>
                  )}

                  {/* iter106 — Check-in Confirmed / No Show — TODAY's check-in date only */}
                  {showCheckinControls(booking) && (
                    <div className="flex items-center gap-2 mb-3" data-testid={`checkin-controls-${booking.booking_id}`}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Guest Identity Document required → open upload modal first, then confirm.
                          if (!booking.has_guest_document) setAadhaarTarget(booking);
                          else setCheckinConfirmTarget(booking);
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-[#EEF1FC] text-[#1B2A7B] border border-[#1B2A7B]/30 text-xs font-semibold hover:bg-[#EEF1FC] transition-colors"
                        data-testid={`checkin-confirm-btn-${booking.booking_id}`}
                      >
                        <LogIn className="w-3.5 h-3.5" /> Check-in Confirmed
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setNoShowTarget(booking); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 text-xs font-semibold hover:bg-red-100 transition-colors"
                        data-testid={`no-show-btn-${booking.booking_id}`}
                      >
                        <UserX className="w-3.5 h-3.5" /> No Show
                      </button>
                    </div>
                  )}

                  {/* iter106+ — "Check-in Pending" badge for past check-in dates with no decision */}
                  {showCheckinPendingBadge(booking) && (
                    <div className="mb-3" data-testid={`checkin-pending-badge-${booking.booking_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                        <Clock className="w-3 h-3" /> Check-in Pending
                      </span>
                    </div>
                  )}

                  {/* iter106 — Check-in decision pills */}
                  {booking.checkin_decision === 'confirmed' && (
                    <div className="mb-3" data-testid={`checkin-decision-${booking.booking_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-[#EEF1FC] text-[#1B2A7B] border border-[#1B2A7B]/30">
                        <LogIn className="w-3 h-3" /> Checked In
                      </span>
                    </div>
                  )}
                  {booking.checkin_decision === 'no_show' && (
                    <div className="mb-3" data-testid={`checkin-decision-${booking.booking_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                        <UserX className="w-3 h-3" /> No Show
                      </span>
                    </div>
                  )}

                  {/* iter92 — Final-state pills (read-only) */}
                  {isOtaBooking(booking) && booking.owner_status === 'Approved' && (
                    <div className="mb-3" data-testid={`approval-status-${booking.booking_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-[#EEF1FC] text-[#1B2A7B] border border-[#1B2A7B]/30">
                        <ShieldCheck className="w-3 h-3" /> Approved by Owner
                      </span>
                    </div>
                  )}
                  {isOtaBooking(booking) && booking.owner_status === 'Rejected' && (
                    <div className="mb-3" data-testid={`approval-status-${booking.booking_id}`}>
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                        <Ban className="w-3 h-3" /> Rejected by Owner
                        {booking.sneha_status === 'Approved' && (
                          <span className="ml-1 px-1.5 py-0.5 rounded bg-[#EEF1FC] text-[#1B2A7B] text-[10px]">Approved</span>
                        )}
                      </span>
                    </div>
                  )}

                  {/* iter111 — Guest WhatsApp Journey badge (OTA only).
                      Hide the default "Not Started" status on card list so
                      the card doesn't carry noise above the OTA logo. */}
                  {isOtaBooking(booking) && (journeyStatuses[booking.booking_id]?.current_status && journeyStatuses[booking.booking_id].current_status !== 'not_started') && (
                    <div className="mb-3" data-testid={`wa-badge-wrap-${booking.booking_id}`}>
                      <WhatsAppJourneyBadge
                        status={journeyStatuses[booking.booking_id].current_status}
                        bookingId={booking.booking_id}
                      />
                    </div>
                  )}
                  
                  {/* Guest Info */}
                  <div className="space-y-2 mb-3">
                    <div className={`flex items-center gap-2 ${isFieldChanged(booking, 'guest_name') ? 'text-blue-700' : 'text-gray-900'}`}>
                      <User className="w-4 h-4 text-[#1B2A7B]" />
                      <span className="font-medium">{booking.guest_name}</span>
                      {isFieldChanged(booking, 'guest_name') && <Edit3 className="w-3 h-3 text-blue-500" />}
                    </div>
                    {booking.ota_source && booking.ota_source !== 'Walk-in' && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <OtaLogo otaName={booking.ota_source} size="lg" />
                      </div>
                    )}
                    {(booking.property_name || booking.master_hotel_name) && (
                      <div className="flex items-center gap-2 text-gray-600 text-sm">
                        <Home className="w-4 h-4 text-gray-400" />
                        <span>{booking.property_name || booking.master_hotel_name}</span>
                      </div>
                    )}
                    <div className={`flex items-center gap-2 text-sm ${isFieldChanged(booking, 'room_type') ? 'text-blue-600' : 'text-gray-600'}`}>
                      <Home className="w-4 h-4 text-gray-400" />
                      <span>{getRoomDisplay(booking.rooms, booking.room_type)}</span>
                      {(() => {
                        const rc = booking.no_of_rooms || booking.room_count || (Array.isArray(booking.rooms) ? booking.rooms.length : 0);
                        return rc > 1 ? <span className="text-xs text-gray-400">x{rc}</span> : null;
                      })()}
                      {isFieldChanged(booking, 'room_type') && <Edit3 className="w-3 h-3 text-blue-500" />}
                    </div>
                    {!isCancelled && (
                      <div className={`flex items-center gap-2 text-sm ${isFieldChanged(booking, 'amount') ? 'text-blue-600' : 'text-gray-600'}`}>
                        <CreditCard className="w-4 h-4 text-gray-400" />
                        <span>₹{(booking.amount || booking.total_amount || 0).toLocaleString()}</span>
                        {isFieldChanged(booking, 'amount') && <Edit3 className="w-3 h-3 text-blue-500" />}
                      </div>
                    )}
                    {isCancelled && (
                      <div className="flex items-center gap-2 text-sm text-red-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span>Amount hidden - Booking cancelled</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Dates */}
                  <div className="flex items-center gap-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    <span className={`flex items-center gap-1 ${isFieldChanged(booking, 'check_in_date') ? 'text-blue-600' : ''}`}>
                      <ArrowDownToLine className="w-3 h-3" />
                      {(booking.check_in_date || (booking.check_in || '').split('T')[0]) || '-'}
                      {isFieldChanged(booking, 'check_in_date') && <Edit3 className="w-2 h-2 text-blue-500" />}
                    </span>
                    <span className={`flex items-center gap-1 ${isFieldChanged(booking, 'check_out_date') ? 'text-blue-600' : ''}`}>
                      <ArrowUpFromLine className="w-3 h-3" />
                      {(booking.check_out_date || (booking.check_out || '').split('T')[0]) || '-'}
                      {isFieldChanged(booking, 'check_out_date') && <Edit3 className="w-2 h-2 text-blue-500" />}
                    </span>
                    {(booking.booked_on_date || booking.booking_date) && (
                      <span className="flex items-center gap-1 ml-auto">
                        <CalendarIcon className="w-3 h-3" />
                        Booked: {(booking.booked_on_date || booking.booking_date || '').split('T')[0]}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Click outside to close notifications */}
      {showNotifications && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowNotifications(false);
          }}
        />
      )}

      {/* Booking Details Popup */}
      {showBookingPopup && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeBookingPopup}>
          <div 
            className={`rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl ${
              selectedBooking.is_cancelled ? 'bg-red-50' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-details-popup"
          >
            {/* Popup Header */}
            <div className={`sticky top-0 text-white px-4 py-4 rounded-t-2xl flex items-center justify-between ${
              selectedBooking.is_cancelled ? 'bg-red-600' : 'bg-navy'
            }`}>
              <div>
                <p className="text-xs text-gray-300">Booking Details</p>
                <p className="font-semibold">{selectedBooking.booking_id}</p>
              </div>
              <button 
                onClick={closeBookingPopup}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="close-popup"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Popup Content */}
            <div className="p-4 space-y-4">
              {/* Cancelled Warning */}
              {selectedBooking.is_cancelled && (
                <div className="bg-red-100 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="font-semibold text-red-800">Booking Cancelled</p>
                    <p className="text-sm text-red-600">This booking has been cancelled</p>
                  </div>
                </div>
              )}
              
              {/* Amendment History */}
              {selectedBooking.has_amendments && selectedBooking.amendment_history?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <History className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">Amendment History</h4>
                  </div>
                  <div className="space-y-2">
                    {selectedBooking.amendment_history.map((change, idx) => (
                      <div key={idx} className="text-sm bg-white rounded-lg p-3 border border-blue-100">
                        <p className="font-medium text-gray-700 capitalize">{change.field.replace(/_/g, ' ')}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-red-600 line-through">{String(change.original_value) || 'N/A'}</span>
                          <span className="text-gray-400">→</span>
                          <span className="text-blue-600 font-medium">{String(change.new_value) || 'N/A'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Guest Info */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Guest Information</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-[#1B2A7B]" />
                    <div>
                      <p className="text-xs text-gray-400">Guest Name</p>
                      <p className={`font-semibold ${isFieldChanged(selectedBooking, 'guest_name') ? 'text-blue-700' : 'text-gray-900'}`}>
                        {selectedBooking.guest_name}
                      </p>
                    </div>
                  </div>
                  {selectedBooking.mobile_number && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#1B2A7B]" />
                      <div>
                        <p className="text-xs text-gray-400">Phone</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.mobile_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Hotel & Source */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Booking Source</h4>
                <div className="space-y-3">
                  {(selectedBooking.property_name || selectedBooking.master_hotel_name) && (
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-[#1B2A7B]" />
                      <div>
                        <p className="text-xs text-gray-400">Hotel</p>
                        <p className="font-semibold text-gray-900">{selectedBooking.property_name || selectedBooking.master_hotel_name}</p>
                      </div>
                    </div>
                  )}
                  {/* iter183 — City sourced verbatim from the stored booking record. */}
                  {selectedBooking.city && (
                    <div className="flex items-center gap-3" data-testid="popup-city-row">
                      <MapPin className="w-5 h-5 text-[#1B2A7B]" />
                      <div>
                        <p className="text-xs text-gray-400">City</p>
                        <p className="font-semibold text-gray-900" data-testid="popup-city">
                          {selectedBooking.city}
                        </p>
                      </div>
                    </div>
                  )}
                  {/* iter191 — OTA / Source: back to inline left-aligned row. */}
                  <div className="flex items-center gap-3" data-testid="popup-ota-block">
                    <Globe className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-xs text-gray-400">OTA / Source</p>
                      <div className="mt-1">
                        <OtaLogo otaName={selectedBooking.ota_source || 'Offline'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Stay Dates</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <ArrowDownToLine className="w-5 h-5 text-[#1B2A7B]" />
                    <div>
                      <p className="text-xs text-gray-400">Check-in</p>
                      <p className={`font-semibold ${isFieldChanged(selectedBooking, 'check_in_date') ? 'text-blue-700' : 'text-gray-900'}`}>
                        {(selectedBooking.check_in_date || (selectedBooking.check_in || '').split('T')[0]) || '-'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <ArrowUpFromLine className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="text-xs text-gray-400">Check-out</p>
                      <p className={`font-semibold ${isFieldChanged(selectedBooking, 'check_out_date') ? 'text-blue-700' : 'text-gray-900'}`}>
                        {(selectedBooking.check_out_date || (selectedBooking.check_out || '').split('T')[0]) || '-'}
                      </p>
                    </div>
                  </div>
                </div>
                {(selectedBooking.booked_on_date || selectedBooking.booking_date) && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-3">
                    <CalendarIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-400">Booking Date</p>
                      <p className="font-semibold text-gray-900">{(selectedBooking.booked_on_date || selectedBooking.booking_date || '').split('T')[0]}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Room Details */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-3">Room Details</h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Home className="w-5 h-5 text-[#1B2A7B]" />
                    <div>
                      <p className="text-xs text-gray-400">Room Type</p>
                      <p className={`font-semibold ${isFieldChanged(selectedBooking, 'room_type') ? 'text-blue-700' : 'text-gray-900'}`}>
                        {getRoomDisplay(selectedBooking.rooms, selectedBooking.room_type)}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#EEF1FC] rounded-lg flex items-center justify-center">
                        <Home className="w-4 h-4 text-[#1B2A7B]" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Rooms</p>
                        <p className="font-semibold text-gray-900" data-testid="popup-no-of-rooms">{selectedBooking.no_of_rooms || selectedBooking.room_count || (Array.isArray(selectedBooking.rooms) ? selectedBooking.rooms.length : (selectedBooking.rooms || 1))}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <User className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Guests</p>
                        {/* iter184 — Render guests exactly as stored on the record. */}
                        <p className="font-semibold text-gray-900" data-testid="popup-no-of-guests">{rawGuestValue(selectedBooking)}</p>
                      </div>
                    </div>
                  </div>
                  {selectedBooking.rate_plan && (
                    <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                      <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400">Rate Plan</p>
                        <p className="font-semibold text-gray-900" data-testid="popup-rate-plan">{selectedBooking.rate_plan}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Amount - Hidden for cancelled */}
              {!selectedBooking.is_cancelled && (
                <div className="bg-[#EEF1FC] rounded-xl p-4 border border-[#1B2A7B]/20">
                  <h4 className="text-sm font-medium text-[#1B2A7B] mb-3">Payment</h4>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-[#1B2A7B]" />
                      <div>
                        <p className="text-xs text-gray-500">Total Amount</p>
                        <p className={`text-2xl font-bold ${isFieldChanged(selectedBooking, 'amount') ? 'text-blue-700' : 'text-[#1B2A7B]'}`} data-testid="popup-amount">
                          ₹{(selectedBooking.amount || selectedBooking.total_amount || 0).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Invoice Actions — only for manual bookings */}
              {isManualBooking(selectedBooking) && !selectedBooking.is_cancelled && (
                <div className="flex items-center gap-3" data-testid="popup-invoice-actions">
                  <button
                    onClick={(e) => { closeBookingPopup(); handleInvoicePreview(e, selectedBooking); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-50 text-blue-700 rounded-xl font-medium hover:bg-blue-100 transition-colors border border-blue-200"
                    data-testid="popup-preview-invoice"
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={(e) => handleInvoiceDownload(e, selectedBooking)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#EEF1FC] text-[#1B2A7B] rounded-xl font-medium hover:bg-[#EEF1FC] transition-colors border border-[#1B2A7B]/30"
                    data-testid="popup-download-invoice"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              )}

              {/* iter92 — Owner Approve / Reject in voucher popup (BEFORE check-in date) */}
              {showApprovalControls(selectedBooking) && (
                <div className="flex items-center gap-3" data-testid="popup-approval-actions">
                  <button
                    onClick={() => setApproveTarget(selectedBooking)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#EEF1FC] text-[#1B2A7B] rounded-xl font-semibold hover:bg-[#EEF1FC] transition-colors border border-[#1B2A7B]/30"
                    data-testid="popup-approve-btn"
                  >
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button
                    onClick={() => { setRejectTarget(selectedBooking); setRejectReason(''); setRejectOther(''); }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-50 text-red-700 rounded-xl font-semibold hover:bg-red-100 transition-colors border border-red-200"
                    data-testid="popup-reject-btn"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              )}

              {/* iter106 — Check-in Confirmed / No Show in voucher popup (ON or AFTER check-in date) */}
              {showCheckinControls(selectedBooking) && (
                <div className="flex items-center gap-3" data-testid="popup-checkin-actions">
                  <button
                    onClick={() => {
                      if (!selectedBooking.has_guest_document) setAadhaarTarget(selectedBooking);
                      else setCheckinConfirmTarget(selectedBooking);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#EEF1FC] text-[#1B2A7B] rounded-xl font-semibold hover:bg-[#EEF1FC] transition-colors border border-[#1B2A7B]/30"
                    data-testid="popup-checkin-confirm-btn"
                  >
                    <LogIn className="w-4 h-4" /> Check-in Confirmed
                  </button>
                  <button
                    onClick={() => setNoShowTarget(selectedBooking)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-50 text-red-700 rounded-xl font-semibold hover:bg-red-100 transition-colors border border-red-200"
                    data-testid="popup-no-show-btn"
                  >
                    <UserX className="w-4 h-4" /> No Show
                  </button>
                </div>
              )}

              {/* iter106+ — "Check-in Pending" badge for past undecided check-ins */}
              {showCheckinPendingBadge(selectedBooking) && (
                <div data-testid="popup-checkin-pending">
                  <span className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <Clock className="w-4 h-4" /> Check-in Pending
                  </span>
                </div>
              )}

              {/* iter122 — Guest Identity Document preview (5 types supported) */}
              {selectedBooking.has_guest_document && (
                <div data-testid="popup-guest-documents">
                  <GuestDocumentCard bookingId={selectedBooking.booking_id} />
                </div>
              )}

              {/* iter132 — Open Guest Chat button removed per owner spec.
                  Chat remains accessible via the dedicated Chats tab. */}

              {/* iter92 — Approval Information section (read-only, only shows populated fields) */}
              {isOtaBooking(selectedBooking) && (selectedBooking.owner_status === 'Approved' || selectedBooking.owner_status === 'Rejected') && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2" data-testid="popup-approval-info">
                  <h4 className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Approval Information</h4>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    <span className="text-slate-500">Owner Status</span>
                    <span className={`font-semibold ${selectedBooking.owner_status === 'Approved' ? 'text-[#1B2A7B]' : 'text-red-700'}`}>{selectedBooking.owner_status}</span>
                    {selectedBooking.approved_by && (<>
                      <span className="text-slate-500">Approved By</span><span className="font-medium text-slate-800">{selectedBooking.approved_by}</span>
                      <span className="text-slate-500">Approved At</span><span className="text-slate-700 text-xs">{selectedBooking.approved_at ? new Date(selectedBooking.approved_at).toLocaleString('en-IN') : '—'}</span>
                    </>)}
                    {selectedBooking.rejected_by && (<>
                      <span className="text-slate-500">Rejected By</span><span className="font-medium text-slate-800">{selectedBooking.rejected_by}</span>
                      <span className="text-slate-500">Rejected At</span><span className="text-slate-700 text-xs">{selectedBooking.rejected_at ? new Date(selectedBooking.rejected_at).toLocaleString('en-IN') : '—'}</span>
                      <span className="text-slate-500">Rejection Reason</span><span className="font-medium text-slate-800">{selectedBooking.rejection_reason}{selectedBooking.other_rejection_reason ? ` — ${selectedBooking.other_rejection_reason}` : ''}</span>
                    </>)}
                    <span className="text-slate-500">Sneha Status</span>
                    <span className={`font-semibold ${selectedBooking.sneha_status === 'Approved' ? 'text-[#1B2A7B]' : 'text-amber-700'}`}>{selectedBooking.sneha_status || 'Pending'}</span>
                    {selectedBooking.sneha_updated_by && (<>
                      <span className="text-slate-500">Sneha Updated By</span><span className="font-medium text-slate-800">{selectedBooking.sneha_updated_by}</span>
                      <span className="text-slate-500">Sneha Updated At</span><span className="text-slate-700 text-xs">{selectedBooking.sneha_updated_at ? new Date(selectedBooking.sneha_updated_at).toLocaleString('en-IN') : '—'}</span>
                    </>)}
                  </div>
                </div>
              )}

              {/* Status — clickable for manual confirmed bookings to trigger cancel */}
              {(() => {
                const info = getStatusInfo(selectedBooking);
                const canCancel = isManualBooking(selectedBooking) && !selectedBooking.is_cancelled;
                return (
                  <>
                    <div className="flex items-center justify-between py-3">
                      <span className="text-sm text-gray-500">Status</span>
                      {canCancel ? (
                        <button
                          type="button"
                          onClick={() => startCancelFlow(selectedBooking)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${info.bg} ${info.text} hover:brightness-95 active:scale-95 transition flex items-center gap-1 cursor-pointer`}
                          title="Tap to cancel booking"
                          data-testid="status-pill-cancellable"
                        >
                          {info.label}
                          <span className="text-[10px] opacity-70">▾</span>
                        </button>
                      ) : (
                        <span
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium ${info.bg} ${info.text}`}
                          data-testid="status-pill"
                        >
                          {info.label}
                        </span>
                      )}
                    </div>
                    {selectedBooking.is_cancelled && (selectedBooking.refund_amount ?? 0) > 0 && (
                      <div className="flex items-center justify-between py-1 -mt-2">
                        <span className="text-sm text-gray-500">Refund</span>
                        <span className="text-sm font-semibold text-red-700" data-testid="popup-refund-amount">
                          ₹{Number(selectedBooking.refund_amount).toLocaleString('en-IN')}
                        </span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Booking Modal — 2 step flow */}
      {cancelStep && cancelTarget && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
          onClick={() => !cancelling && setCancelStep(null)}
          data-testid="cancel-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Booking · {cancelTarget.booking_id}
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">
                {cancelStep === 'confirm' ? 'Cancel this booking?' : 'Refund amount (optional)'}
              </h3>
            </div>

            <div className="p-5">
              {cancelStep === 'confirm' ? (
                <p className="text-sm text-gray-600">
                  Are you sure you want to cancel this booking?
                  <br />
                  <span className="text-gray-500">A WhatsApp notification will be sent to the guest.</span>
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-3">
                    Enter the amount you want to refund the guest. Leave 0 if no refund.
                  </p>
                  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:border-[#1B2A7B] focus-within:ring-2 focus-within:ring-emerald/20">
                    <span className="text-gray-500 font-medium">₹</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="1"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="flex-1 outline-none text-lg font-semibold text-gray-900 bg-transparent"
                      data-testid="refund-amount-input"
                      autoFocus
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 p-4 border-t border-gray-100 bg-gray-50">
              {cancelStep === 'confirm' ? (
                <>
                  <button
                    type="button"
                    onClick={() => { setCancelStep(null); setCancelTarget(null); }}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100"
                    data-testid="cancel-go-back"
                  >
                    No, Go Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setCancelStep('refund')}
                    className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
                    data-testid="cancel-yes"
                  >
                    Yes, Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    disabled={cancelling}
                    onClick={() => setCancelStep('confirm')}
                    className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-100 disabled:opacity-50"
                    data-testid="cancel-back"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={cancelling}
                    onClick={submitCancel}
                    className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="confirm-cancel-btn"
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Confirm Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* FAB for new booking */}
      <button
        onClick={() => navigate('/booking/new')}
        className="fixed bottom-24 right-4 w-14 h-14 rounded-full bg-[#1B2A7B] flex items-center justify-center shadow-[#1B2A7B]/30 hover:bg-[#1B2A7B]-dark transition-colors z-20"
        data-testid="new-booking-fab"
      >
        <Plus className="w-6 h-6 text-white" />
      </button>

      {/* Invoice Preview Modal */}
      {invoicePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setInvoicePreview(null)}>
          <div 
            className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="invoice-preview-modal"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="font-semibold text-gray-900">Invoice Preview</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const token = getToken();
                    const url = `${bookingsAPI.getInvoiceDownloadUrl(invoicePreview.bookingId)}&token=${token}`;
                    window.open(url, '_blank');
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[#1B2A7B] text-white text-sm rounded-lg hover:bg-[#1B2A7B]-dark transition-colors"
                  data-testid="modal-download-pdf"
                >
                  <Download className="w-3.5 h-3.5" />
                  PDF
                </button>
                <button 
                  onClick={() => setInvoicePreview(null)}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                  data-testid="close-invoice-modal"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <iframe
                src={invoicePreview.url}
                className="w-full h-[75vh] border-0"
                title="Invoice Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* iter92 — Owner Approve confirmation modal */}
      {approveTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60"
          onClick={() => !approvalSubmitting && setApproveTarget(null)}
          data-testid="approve-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Booking · {approveTarget.booking_id}
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">Approve this booking?</h3>
              <p className="text-sm text-gray-500 mt-1">
                Once approved, this decision is final and cannot be undone.
              </p>
            </div>
            <div className="px-5 py-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setApproveTarget(null)}
                disabled={approvalSubmitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition disabled:opacity-60"
                data-testid="approve-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitOwnerApprove}
                disabled={approvalSubmitting}
                className="flex-1 py-2.5 rounded-lg bg-[#152163] hover:bg-[#152163] text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-60"
                data-testid="approve-modal-confirm"
              >
                {approvalSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Confirm Approval
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter92 — Owner Reject reason modal */}
      {rejectTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60"
          onClick={() => {
            if (approvalSubmitting) return;
            setRejectTarget(null); setRejectReason(''); setRejectOther('');
          }}
          data-testid="reject-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-500">
                Booking · {rejectTarget.booking_id}
              </p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">Reject Booking</h3>
              <p className="text-sm text-gray-500 mt-1">Select a reason to continue.</p>
            </div>
            <div className="px-5 py-4 space-y-2">
              {['Property Sold Out', 'Net Rate Issue', 'Payment Issue', 'Other'].map((opt) => (
                <label
                  key={opt}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
                    rejectReason === opt ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  data-testid={`reject-reason-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <input
                    type="radio"
                    name="reject-reason"
                    value={opt}
                    checked={rejectReason === opt}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="accent-red-600"
                  />
                  <span className="text-sm text-gray-800">{opt}</span>
                </label>
              ))}
              {rejectReason === 'Other' && (
                <textarea
                  value={rejectOther}
                  onChange={(e) => setRejectOther(e.target.value)}
                  placeholder="Please describe the reason..."
                  rows={3}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-800 focus:border-red-400 focus:ring-1 focus:ring-red-400 outline-none resize-none"
                  data-testid="reject-other-textarea"
                />
              )}
            </div>
            <div className="px-5 pb-5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => { setRejectTarget(null); setRejectReason(''); setRejectOther(''); }}
                disabled={approvalSubmitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition disabled:opacity-60"
                data-testid="reject-modal-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitOwnerReject}
                disabled={
                  approvalSubmitting ||
                  !rejectReason ||
                  (rejectReason === 'Other' && !rejectOther.trim())
                }
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2 transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="reject-modal-confirm"
              >
                {approvalSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                Reject Booking
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter122 — Guest Identity Document capture modal (Aadhaar/PAN/Passport/DL/Voter ID) */}
      {aadhaarTarget && (
        <AadhaarCaptureModal
          booking={aadhaarTarget}
          onClose={() => setAadhaarTarget(null)}
          onUploaded={(doc) => {
            // Patch the booking in-place so the UI reflects upload immediately.
            const patched = { ...aadhaarTarget, has_guest_document: true, guest_document_id: doc?.id };
            setSelectedBooking((prev) => (prev && prev.booking_id === patched.booking_id ? { ...prev, ...patched } : prev));
            setAadhaarTarget(null);
            // If we opened the modal as a check-in pre-requisite, surface the confirm dialog next.
            if (isCheckinDateOrPast(patched) && !patched.checkin_decision) {
              setCheckinConfirmTarget(patched);
            }
            try { refreshData?.(); } catch (_) { /* ignore */ }
          }}
        />
      )}

      {/* iter106 — Check-in Confirm confirmation modal */}
      {checkinConfirmTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60"
          onClick={() => !checkinSubmitting && setCheckinConfirmTarget(null)}
          data-testid="checkin-confirm-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-500">Booking · {checkinConfirmTarget.booking_id}</p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">Confirm guest check-in?</h3>
              <p className="text-sm text-gray-500 mt-1">Guest identity document on file. This will mark the booking as Checked In.</p>
            </div>
            <div className="px-5 py-4 flex items-center gap-2">
              <button
                onClick={() => setCheckinConfirmTarget(null)}
                disabled={checkinSubmitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="checkin-confirm-cancel"
              >Cancel</button>
              <button
                onClick={submitCheckinConfirm}
                disabled={checkinSubmitting}
                className="flex-1 py-2.5 rounded-lg bg-[#152163] hover:bg-[#152163] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="checkin-confirm-submit"
              >
                {checkinSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                Confirm Check-in
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iter106 — No Show confirmation modal */}
      {noShowTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60"
          onClick={() => !checkinSubmitting && setNoShowTarget(null)}
          data-testid="no-show-modal"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-gray-100">
              <p className="text-xs uppercase tracking-wider text-gray-500">Booking · {noShowTarget.booking_id}</p>
              <h3 className="text-lg font-semibold text-gray-900 mt-1">Mark guest as No Show?</h3>
              <p className="text-sm text-gray-500 mt-1">This action cannot be undone.</p>
            </div>
            <div className="px-5 py-4 flex items-center gap-2">
              <button
                onClick={() => setNoShowTarget(null)}
                disabled={checkinSubmitting}
                className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-60"
                data-testid="no-show-cancel"
              >Cancel</button>
              <button
                onClick={submitNoShow}
                disabled={checkinSubmitting}
                className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="no-show-submit"
              >
                {checkinSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                Mark No Show
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarScreen;
